import { useEffect, useRef, useState } from "react";
import { Client } from "../../../models/ClientModels";
import { Branch } from "../../../models/BranchModels";
import { User } from "../../../models/User";
import { Service } from "../../../models/ServiceModels";
import { FollowUp, FollowUpStatus } from "../../../models/FollowUpModels";
import { toDateTimeLocal } from "../../../utils/DateTime";
import "./FollowUpModal.css";

export type FollowUpModalMode = "create" | "edit";

interface FollowUpModalProps {
  isOpen: boolean;
  mode: FollowUpModalMode;
  followUp: FollowUp | null;
  branches: Branch[];
  services: Service[];
  staff: User[];
  submitting: boolean;
  lookupClient: (phone: string) => Promise<Client | null>;
  appointmentClient?: {
    fullName?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    age?: number | null;
  } | null;
  preselectedClient?: Client | null;
  initial?: { clientId?: string; appointmentId?: string; serviceId?: string };
  onNewClient: (phone: string) => void;
  canCreateClient: boolean;
  onClose: () => void;
  onSubmit: (form: FollowUpFormValues) => Promise<void>;
}

export interface FollowUpFormValues {
  clientId: string;
  branchId: string;
  appointmentId: string;
  serviceId: string;
  title: string;
  notes: string;
  dueAt: string;
  status: string;
  priority: string;
  assignedTo: string;
  assignedName: string;
  lastActionNote: string;
}

const emptyForm: FollowUpFormValues = {
  clientId: "",
  branchId: "",
  appointmentId: "",
  serviceId: "",
  title: "",
  notes: "",
  dueAt: "",
  status: String(FollowUpStatus.Pending),
  priority: "2",
  assignedTo: "",
  assignedName: "",
  lastActionNote: "",
};

const fromFollowUp = (followUp: FollowUp): FollowUpFormValues => ({
  clientId: followUp.clientId,
  branchId: followUp.branchId || "",
  appointmentId: followUp.appointmentId || "",
  serviceId: followUp.serviceId || "",
  title: followUp.title || "",
  notes: followUp.notes || "",
  dueAt: toDateTimeLocal(followUp.dueAt),
  status: String(followUp.status),
  priority: String(followUp.priority || 2),
  assignedTo: followUp.assignedTo || "",
  assignedName: followUp.assignedName || "",
  lastActionNote: "",
});

type LookupStatus = "idle" | "loading" | "found" | "not-found" | "error";

const FollowUpModal = ({
  isOpen,
  mode,
  followUp,
  branches,
  services,
  staff,
  submitting,
  lookupClient,
  appointmentClient,
  preselectedClient,
  initial,
  onNewClient,
  canCreateClient,
  onClose,
  onSubmit,
}: FollowUpModalProps) => {
  const [form, setForm] = useState<FollowUpFormValues>(emptyForm);
  const [phoneInput, setPhoneInput] = useState("");
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>("idle");
  const [foundClient, setFoundClient] = useState<Client | null>(null);

  const branchesRef = useRef(branches);
  branchesRef.current = branches;

  useEffect(() => {
    if (!isOpen) return;
    setPhoneInput("");
    setFoundClient(null);
    setLookupStatus("idle");
    if (mode === "create") {
      setForm({
        ...emptyForm,
        branchId: branchesRef.current[0]?.id ?? "",
        clientId: initial?.clientId ?? "",
        appointmentId: initial?.appointmentId ?? "",
        serviceId: initial?.serviceId ?? "",
      });
    } else if (followUp) {
      setForm(fromFollowUp(followUp));
    }
  }, [isOpen, mode, followUp, initial?.clientId, initial?.appointmentId, initial?.serviceId]);

  useEffect(() => {
    if (!isOpen || mode !== "create" || !preselectedClient) return;
    setFoundClient(preselectedClient);
    setPhoneInput(preselectedClient.phone ?? "");
    setLookupStatus("found");
    setForm((prev) => ({ ...prev, clientId: preselectedClient.id }));
  }, [isOpen, mode, preselectedClient]);

  if (!isOpen) return null;

  const setField = (key: keyof FollowUpFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetLookup = () => {
    setLookupStatus("idle");
    setFoundClient(null);
    setForm((prev) => ({ ...prev, clientId: "" }));
  };

  const handleLookup = async () => {
    const phone = phoneInput.trim();
    if (!phone) return;
    setLookupStatus("loading");
    try {
      const client = await lookupClient(phone);
      if (client) {
        setFoundClient(client);
        setLookupStatus("found");
        setForm((prev) => ({ ...prev, clientId: client.id }));
      } else {
        setFoundClient(null);
        setLookupStatus("not-found");
        setForm((prev) => ({ ...prev, clientId: "" }));
      }
    } catch {
      setFoundClient(null);
      setLookupStatus("error");
      setForm((prev) => ({ ...prev, clientId: "" }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  const clientKnown = mode === "edit" || Boolean(initial?.clientId);

  return (
    <div className="fu-modal-overlay">
      <div className="fu-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fu-modal__header">
          <h3>{mode === "create" ? "Add Follow-Up" : "Edit Follow-Up"}</h3>
          <button
            className="fu-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="fu-modal__body" onSubmit={handleSubmit}>
          <div className="fu-form-grid">
            <div className="fu-form-group fu-form-group--full">
              <label htmlFor="fu-client-phone">
                {clientKnown ? "Client" : "Client Phone *"}
              </label>

              {clientKnown ? (
                <div className="fu-client-card">
                  <div className="fu-client-card__name">
                    {appointmentClient?.fullName ?? "Unknown client"}
                  </div>
                  {appointmentClient?.phone && (
                    <div className="fu-client-card__meta">
                      <span>Phone · {appointmentClient.phone}</span>
                    </div>
                  )}
                  {appointmentClient?.age != null && (
                    <div className="fu-client-card__meta">
                      <span>Age · {appointmentClient.age}</span>
                    </div>
                  )}
                  {appointmentClient?.address && (
                    <div className="fu-client-card__meta">
                      <span>Address · {appointmentClient.address}</span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="fu-client-row">
                    <input
                      id="fu-client-phone"
                      type="tel"
                      value={phoneInput}
                      placeholder="Enter client phone number"
                      onChange={(e) => {
                        setPhoneInput(e.target.value);
                        if (lookupStatus !== "idle") resetLookup();
                      }}
                    />
                    <button
                      type="button"
                      className="fu-btn fu-btn--secondary fu-btn--compact"
                      disabled={!phoneInput.trim() || lookupStatus === "loading"}
                      onClick={handleLookup}
                    >
                      {lookupStatus === "loading" ? "Searching…" : "Search"}
                    </button>
                  </div>

                  {lookupStatus === "loading" && (
                    <p className="fu-form-hint">Looking up client…</p>
                  )}

                  {lookupStatus === "not-found" && (
                    <div className="fu-lookup-empty">
                      <p className="fu-form-error">
                        No client found with this number.
                      </p>
                      {canCreateClient ? (
                        <button
                          type="button"
                          className="fu-btn fu-btn--primary fu-btn--compact"
                          onClick={() => onNewClient(phoneInput.trim())}
                        >
                          Add as New Client
                        </button>
                      ) : (
                        <p className="fu-form-error">
                          You don't have permission to add new clients.
                        </p>
                      )}
                    </div>
                  )}

                  {lookupStatus === "error" && (
                    <p className="fu-form-error">
                      Could not search. Please try again.
                    </p>
                  )}

                  {foundClient && lookupStatus === "found" && (
                    <div className="fu-client-card">
                      <div className="fu-client-card__name">
                        {foundClient.fullName}
                      </div>
                      <div className="fu-client-card__meta">
                        <span>Phone · {foundClient.phone}</span>
                        {foundClient.age != null && (
                          <span>Age · {foundClient.age}</span>
                        )}
                        {foundClient.gender && (
                          <span>Gender · {foundClient.gender}</span>
                        )}
                        {foundClient.address && (
                          <span>Address · {foundClient.address}</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="fu-form-group">
              <label htmlFor="fu-branch">Branch</label>
              <select
                id="fu-branch"
                value={form.branchId}
                onChange={(e) => setField("branchId", e.target.value)}
              >
                <option value="">—</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="fu-form-group">
              <label htmlFor="fu-service">Service</label>
              <select
                id="fu-service"
                value={form.serviceId}
                onChange={(e) => setField("serviceId", e.target.value)}
              >
                <option value="">—</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="fu-form-group">
              <label htmlFor="fu-status">Status</label>
              <select
                id="fu-status"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                <option value={FollowUpStatus.Pending}>Pending</option>
                <option value={FollowUpStatus.Contacted}>Contacted</option>
                <option value={FollowUpStatus.Completed}>Completed</option>
                <option value={FollowUpStatus.Cancelled}>Cancelled</option>
                <option value={FollowUpStatus.Skipped}>Skipped</option>
              </select>
            </div>

            <div className="fu-form-group">
              <label htmlFor="fu-due">Due Date &amp; Time *</label>
              <div className="fu-datetime-row">
                <input
                  id="fu-due"
                  type="datetime-local"
                  value={form.dueAt}
                  onChange={(e) => setField("dueAt", e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="fu-btn fu-btn--secondary fu-btn--compact"
                  onClick={() =>
                    setField(
                      "dueAt",
                      form.dueAt || toDateTimeLocal(new Date().toISOString()),
                    )
                  }
                >
                  OK
                </button>
              </div>
            </div>

            <div className="fu-form-group">
              <label htmlFor="fu-priority">Priority</label>
              <select
                id="fu-priority"
                value={form.priority}
                onChange={(e) => setField("priority", e.target.value)}
              >
                <option value="1">Low</option>
                <option value="2">Medium</option>
                <option value="3">High</option>
              </select>
            </div>

            <div className="fu-form-group">
              <label htmlFor="fu-assigned">Assigned To</label>
              <select
                id="fu-assigned"
                value={form.assignedTo}
                onChange={(e) => {
                  setField("assignedTo", e.target.value);
                  const selectedUser = staff.find((u) => u.id === e.target.value);
                  setField("assignedName", selectedUser?.userName || "");
                }}
              >
                <option value="">—</option>
                {staff.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.userName || user.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="fu-form-group fu-form-group--full">
              <label htmlFor="fu-title">Title</label>
              <input
                id="fu-title"
                type="text"
                value={form.title}
                placeholder="e.g. Re-checkup, result follow-up"
                onChange={(e) => setField("title", e.target.value)}
              />
            </div>
          </div>

          <div className="fu-form-group fu-form-group--full">
            <label htmlFor="fu-notes">Notes</label>
            <textarea
              id="fu-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
            />
          </div>

          {mode === "edit" && (
            <div className="fu-form-group fu-form-group--full">
              <label htmlFor="fu-action">Action Note</label>
              <textarea
                id="fu-action"
                rows={2}
                placeholder="Record what was done (e.g. called, no answer)"
                value={form.lastActionNote}
                onChange={(e) => setField("lastActionNote", e.target.value)}
              />
            </div>
          )}

          <div className="fu-modal__actions">
            <button
              type="button"
              className="fu-btn fu-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="fu-btn fu-btn--primary"
              disabled={submitting || !form.clientId || !form.dueAt}
            >
              {submitting
                ? "Saving..."
                : mode === "create"
                  ? "Add Follow-Up"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FollowUpModal;